// components/AI/AIEvalCases.js — sub-tab "Test Set" di Review Findings.
//
// Regression test untuk text-to-SQL (backend/aiImprove.js): pertanyaan + SQL
// "jawaban benar". Eval menanyakan AI Service tanpa cache lalu membandingkan
// baris hasil SQL model dengan baris hasil SQL golden (nama kolom & urutan
// baris diabaikan). Teks UI berbahasa Inggris, langsung di sini.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../context/AuthContext';
import {
  Loader2, RefreshCw, Play, Plus, Trash2, Download, ChevronDown, ChevronUp,
  CheckCircle2, XCircle, AlertTriangle, CircleDashed,
} from 'lucide-react';

const ST = {
  pass:  { icon: CheckCircle2,  cls: 'text-emerald-600', label: 'Pass' },
  fail:  { icon: XCircle,       cls: 'text-red-500',     label: 'Fail' },
  error: { icon: AlertTriangle, cls: 'text-amber-600',   label: 'Error' },
};
function fmtTime(s) {
  if (!s) return '-';
  const str = String(s);
  const d = new Date(str.includes('T') ? str : str.replace(' ', 'T') + 'Z');
  return isNaN(d) ? str : d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

export function CaseForm({ sources, initial = {}, onSaved, onCancel }) {
  const [f, setF] = useState({ source: initial.source || '', question: initial.question || '',
    check_type: 'sql_result', expected_sql: initial.expected_sql || '', expected_text: '', finding_id: initial.finding_id || null });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setBusy(true); setErr('');
    try { await api.post('/api/ai/improve/cases', f); onSaved && onSaved(); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };
  const inp = 'w-full text-xs border border-slate-200 rounded-lg p-2 bg-white focus:outline-none focus:ring-1 focus:ring-brand-400';
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 grid gap-2">
      <div className="grid md:grid-cols-3 gap-2">
        <select value={f.source} onChange={set('source')} className={inp}>
          <option value="">Select SQL source…</option>
          {(sources || []).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={f.check_type} onChange={set('check_type')} className={inp}>
          <option value="sql_result">Compare query results with expected SQL</option>
          <option value="answer_contains">Answer must contain text</option>
        </select>
      </div>
      <input value={f.question} onChange={set('question')} placeholder="Question exactly as a user would ask it" className={inp} />
      {f.check_type === 'sql_result'
        ? <textarea value={f.expected_sql} onChange={set('expected_sql')} rows={5} placeholder="Expected SQL (the correct query). It is run now to validate it."
            className={inp + ' font-mono'} />
        : <textarea value={f.expected_text} onChange={set('expected_text')} rows={2} placeholder="Text the answer must contain — one item per line"
            className={inp} />}
      {err && <div className="text-xs text-red-600">{err}</div>}
      <div className="flex justify-end gap-2">
        {onCancel && <button onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1">Cancel</button>}
        <button onClick={save} disabled={busy || !f.source || !f.question}
          className="text-xs bg-brand-600 hover:bg-brand-700 text-white rounded-lg px-3 py-1.5 disabled:opacity-50 flex items-center gap-1">
          {busy && <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} />} Save test case
        </button>
      </div>
    </div>
  );
}

function CaseRow({ c, onChange }) {
  const [open, setOpen] = useState(false);
  const s = ST[c.last_status] || { icon: CircleDashed, cls: 'text-slate-300', label: 'Not run' };
  const Icon = s.icon;
  const toggle = async () => { await api.patch(`/api/ai/improve/cases/${c.id}`, { enabled: c.enabled ? 0 : 1 }); onChange(); };
  const del = async () => { if (window.confirm('Delete this test case?')) { await api.delete(`/api/ai/improve/cases/${c.id}`); onChange(); } };
  const run = async () => { await api.post('/api/ai/improve/eval', { case_ids: [c.id] }); onChange(true); };
  return (
    <div className={`border-t border-slate-100 ${c.enabled ? '' : 'opacity-50'}`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <Icon style={{ width: 14, height: 14 }} className={`${s.cls} flex-shrink-0`} title={s.label} />
        <button onClick={() => setOpen(o => !o)} className="flex-1 min-w-0 text-left">
          <div className="text-xs text-slate-800 truncate">{c.question}</div>
          <div className="text-[10px] text-slate-400">
            <span className="font-mono">{c.source}</span> · {c.origin}{c.last_run_at ? ` · ${fmtTime(c.last_run_at)}` : ''}
          </div>
        </button>
        <button onClick={run} title="Run" className="text-slate-400 hover:text-brand-600 p-1"><Play style={{ width: 13, height: 13 }} /></button>
        <button onClick={toggle} title={c.enabled ? 'Disable' : 'Enable'}
          className="text-[10px] text-slate-500 border border-slate-200 rounded px-1.5 py-0.5 hover:bg-slate-50">{c.enabled ? 'On' : 'Off'}</button>
        <button onClick={del} title="Delete" className="text-slate-300 hover:text-red-500 p-1"><Trash2 style={{ width: 13, height: 13 }} /></button>
        <button onClick={() => setOpen(o => !o)} className="text-slate-400 p-1">
          {open ? <ChevronUp style={{ width: 13, height: 13 }} /> : <ChevronDown style={{ width: 13, height: 13 }} />}
        </button>
      </div>
      {open && (
        <div className="px-3 pb-3 grid gap-2">
          {c.last_detail && <div className={`text-[11px] ${s.cls}`}>{c.last_detail}</div>}
          <div>
            <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{c.check_type === 'sql_result' ? 'Expected SQL' : 'Answer must contain'}</div>
            <pre className="text-[11px] font-mono bg-slate-50 rounded-lg p-2 whitespace-pre-wrap break-words text-slate-700">{c.expected_sql || c.expected_text}</pre>
          </div>
          {c.last_sql && (
            <div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">SQL generated in last run</div>
              <pre className="text-[11px] font-mono bg-slate-50 rounded-lg p-2 whitespace-pre-wrap break-words text-slate-700">{c.last_sql}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AIEvalCases() {
  const [cases, setCases] = useState([]);
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [source, setSource] = useState('');
  const [onlyFailing, setOnlyFailing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const timer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, r] = await Promise.all([
        api.get('/api/ai/improve/cases'), api.get('/api/ai/improve/status'), api.get('/api/ai/improve/runs', { params: { limit: 8 } }),
      ]);
      setCases(c.data.items || []); setStatus(s.data); setRuns(r.data.items || []);
    } catch (e) { setMsg(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const running = status?.eval_running || runs.some(r => r.status === 'running');
  useEffect(() => {
    clearTimeout(timer.current);
    if (running) timer.current = setTimeout(load, 4000);
    return () => clearTimeout(timer.current);
  }, [running, runs, load]);

  const runEval = async () => {
    setMsg('');
    try { await api.post('/api/ai/improve/eval', { source: source || null }); await load(); }
    catch (e) { setMsg(e.response?.data?.error || e.message); }
  };
  const importFs = async () => {
    setMsg('');
    try { const r = await api.post('/api/ai/improve/cases/import-few-shot', { source: source || null }); setMsg(`Imported ${r.data.added}, skipped ${r.data.skipped}.`); await load(); }
    catch (e) { setMsg(e.response?.data?.error || e.message); }
  };

  const shown = cases.filter(c => (!source || c.source === source) && (!onlyFailing || c.last_status === 'fail' || c.last_status === 'error'));
  const en = cases.filter(c => c.enabled && (!source || c.source === source));
  const n = (st) => en.filter(c => c.last_status === st).length;
  const bySrc = {};
  cases.forEach(c => { bySrc[c.source] = bySrc[c.source] || { total: 0, fail: 0 }; bySrc[c.source].total++; if (c.last_status === 'fail' || c.last_status === 'error') bySrc[c.source].fail++; });
  const last = runs[0];

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded-xl border border-slate-100 px-4 py-3">
          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Active cases</div>
          <div className="text-xl font-bold text-slate-800">{en.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 px-4 py-3">
          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Passing</div>
          <div className="text-xl font-bold text-emerald-600">{n('pass')}<span className="text-sm text-slate-400 font-normal"> / {en.length}</span></div>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 px-4 py-3">
          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Failing / error</div>
          <div className="text-xl font-bold text-red-500">{n('fail')} <span className="text-amber-600">/ {n('error')}</span></div>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 px-4 py-3">
          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Last run</div>
          <div className="text-sm font-bold text-slate-800 mt-1">
            {last ? <>{last.status === 'running' && <Loader2 className="inline animate-spin mr-1" style={{ width: 12, height: 12 }} />}
              {last.passed}/{last.total} pass</> : 'never'}
          </div>
          <div className="text-[11px] text-slate-400 truncate">{last ? `${fmtTime(last.started_at)} · ${last.source_filter || 'all sources'} · ${last.phase || last.trigger}` : ''}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <select value={source} onChange={e => setSource(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
          <option value="">All sources ({cases.length})</option>
          {Object.entries(bySrc).sort().map(([s, x]) => <option key={s} value={s}>{s} ({x.total}{x.fail ? `, ${x.fail} failing` : ''})</option>)}
        </select>
        <label className="text-xs text-slate-600 flex items-center gap-1">
          <input type="checkbox" checked={onlyFailing} onChange={e => setOnlyFailing(e.target.checked)} /> Only failing
        </label>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {msg && <span className="text-[11px] text-slate-500">{msg}</span>}
          <button onClick={() => setAdding(a => !a)} className="text-xs px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 flex items-center gap-1">
            <Plus style={{ width: 13, height: 13 }} /> Add case
          </button>
          <button onClick={importFs} className="text-xs px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 flex items-center gap-1"
            title="Turn current few-shot examples into smoke tests">
            <Download style={{ width: 13, height: 13 }} /> Import few-shot
          </button>
          <button onClick={runEval} disabled={running}
            className="text-xs px-3 py-1.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white flex items-center gap-1 disabled:opacity-50">
            {running ? <Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> : <Play style={{ width: 13, height: 13 }} />}
            {running ? 'Running…' : source ? `Run ${source}` : 'Run all'}
          </button>
          <button onClick={load} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-600 hover:bg-slate-50">
            <RefreshCw style={{ width: 13, height: 13 }} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      {adding && <div className="mb-3"><CaseForm sources={status?.sources} initial={{ source }} onSaved={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} /></div>}

      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="px-3 py-2 text-[11px] text-slate-400">
          A case passes when the assistant's query returns the same rows as the expected SQL (column names and row order are ignored).
          Cases imported from few-shot are smoke tests — the model sees them in its prompt.
        </div>
        {shown.map(c => <CaseRow key={c.id} c={c} onChange={() => load()} />)}
        {!shown.length && <div className="text-sm text-slate-400 p-6 text-center border-t border-slate-100">No test cases.</div>}
      </div>
    </div>
  );
}
